/**
 * Collection of helper functions to be distributed amongst other templates in hopes to reduce duplicated code.
 */

import {
    XMLHttpRequestHandler,
    XMLHttpRequestHandler_requestType
} from "../../vendor/valnet/valnet_jslib/XMLHttpRequestHandler/XMLHttpRequestHandler";
import EmbedsSizesGenerator from "./embeds/EmbedsSizesGenerator";
import { EmbedInfo, Result, ViewportSize } from "./embeds/types";
import { CallerWithEmbedRequestReturn } from "./types/caller";

export default class EmbedHelper {
    private embedGeneration: EmbedsSizesGenerator;
    private idBrand: number|null;
    private caller: CallerWithEmbedRequestReturn;
    private embedType_options: Array<embedType>;

    constructor(caller: CallerWithEmbedRequestReturn) {
        this.caller = caller;
        this.idBrand = caller.getIdBrand();
        this.embedType_options = embedTypesList();
    }

    public setIdBrand(idBrand: number|null)
    {
        this.idBrand = idBrand;
    }

    public getEmbedType_options(): Array<embedType>
    {
        return this.embedType_options;
    }

    public setEmbedType_options(options: Array<embedType>)
    {
        this.embedType_options = options;
    }

    /**
     * Generates an embed code based on the provided embed data.
     * Caller must meet the CallerWithEmbedRequestReturn type.
     *
     * @param source Html source of the embed.
     * @param embedType Type of embed. e.g. 'youtube',
     * @param embedContext Context of the embed. Optional.
     */
    public generateEmbed = (
        source: string,
        embedType: string,
        embedContext: string|null = null
    ) => {

        let usrReq_url: string = '/api/embed/get/';
        let usrReq_params: Array<Array<string>> = [];

        let data: embedRequest = {
            embed: source,
            idBrand: this.idBrand,
            embedType,
            embedTypeOptions: embedTypesList().map((embedType) => embedType.type),
        };

        if (embedContext) {
            data.embedContext = embedContext;
        }

        usrReq_params.push(["data", JSON.stringify(data)]);
        this.caller.XHRHdl = new XMLHttpRequestHandler(usrReq_url, usrReq_params, this.caller);
        this.caller.XHRHdl.mode = XMLHttpRequestHandler_requestType.POST;
        this.caller.XHRHdl.onReadyStateFunction = this.caller.onGenerateEmbedRequestReturn;
        this.caller.XHRHdl.execute();
    }

    public buildEmbedWithScript = (embedData: EmbedInfo): string => {

        const parser = new DOMParser();
        let markup = embedData.embed;

        if (embedData.script) {
            markup += embedData.script;
        }
        const html = parser.parseFromString(markup, 'text/html');

        return html.body.innerHTML;
    }

    public buildEmbedHtml = (embedData: EmbedInfo, embedStyle: Array<Result>): string => {

        const parser = new DOMParser();
        const html = parser.parseFromString(embedData.html, 'text/html');
        const embed = html.querySelector(".emaki-embed");

        // @ts-ignore
        embed.dataset.sizes = JSON.stringify(embedStyle);

        return html.body.innerHTML;
    }

    public processContentEmbed = async (embedData: EmbedInfo, viewportSizes: Array<ViewportSize> = null): Promise<string> => {
        if (embedData.name == 'youtube') {
            return embedData.html;
        }

        if(embedData.name == 'valnet_map') {
            let viewportSizes = [];
            let styleMatches = embedData.html.match(new RegExp(`<style>[^<]*</style>`, "ig"));
            let mapStyles = [];
            if(styleMatches && styleMatches.length > 0) {
                mapStyles = styleMatches.filter((styleMatch) => {
                    return styleMatch.match(new RegExp(`#${embedData.id}`, "ig"));
                });
            }

            if(mapStyles && mapStyles.length > 0) {
                mapStyles.forEach((mapStyle) => {
                    let mapHeightMatches = mapStyle.match(new RegExp(`height:\s*([^;]+);`, "i"));
                    if(mapHeightMatches && mapHeightMatches.length > 1) {
                        viewportSizes.push({width: "100%", height: mapHeightMatches[1]});
                    }
                });
            }

            if(viewportSizes.length == 0) {
                viewportSizes.push({width: "100%", height: "80vh"});
            }

            return this.buildEmbedHtml(embedData, viewportSizes);
        }

        let markup = this.buildEmbedWithScript(embedData);

        viewportSizes = viewportSizes || this.defaultViewportSizes();

        this.embedGeneration = new EmbedsSizesGenerator(markup, viewportSizes);
        let embedStyle = await this.embedGeneration.run();

        if (this.isFixedHeightEmbed(embedData.name)) {
            embedStyle.map((es) => es.height = 180);
        }

        if (embedData.name == 'instagram') {

            embedStyle.map((es) => es.height += 50);
        }

        return this.buildEmbedHtml(embedData, embedStyle);
    }

    protected defaultViewportSizes = (): Array<ViewportSize> => {
        return [
            { width: 750, height: 1024 },
            { width: 375, height: 667 }
        ];
    }

    public isFixedHeightEmbed = (embedName: string): boolean => {
        return (['play_store_app', 'edge', 'fdroid', 'gog', 'huawei', 'microsoft_store', 'snapcraft'].indexOf(embedName) !== -1);
    }

    public abortEmbedGeneration = (): void => {
        if (this.embedGeneration) {
            this.embedGeneration.abort();
        }
    }
};

export const embedTypesList = (): Array<embedType> => {
    const embedTypes: Array<embedType> = [
        { 'name': 'All', 'type': 'all' },
        { 'name': 'Spotify Podcasters', 'type': 'embed_anchor' },
        { 'name': 'Edge Add-Ons', 'type': 'embed_edge' },
        { 'name': 'Facebook', 'type': 'embed_facebook' },
        { 'name': 'F-Droid', 'type': 'embed_fdroid' },
        { 'name': 'Gfycat', 'type': 'embed_gfycat' },
        { 'name': 'Gleam', 'type': 'embed_gleam' },
        { 'name': 'GOG.com', 'type': 'embed_gog' },
        { 'name': 'Huawei AppGallery', 'type': 'embed_huawei' },
        { 'name': 'Instagram', 'type': 'embed_instagraarticlesEdit_template_new.tsm' },
        { 'name': 'LinkedIn', 'type': 'embed_linkedin' },
        { 'name': 'Mastodon', 'type': 'embed_mastodon' },
        { 'name': 'Megaphone', 'type': 'embed_megaphone' },
        { 'name': 'Microsoft Store', 'type': 'embed_microsoft_store' },
        { 'name': 'Play Store App', 'type': 'embed_play_store_app' },
        { 'name': 'Reddit', 'type': 'embed_reddit' },
        { 'name': 'Reelgood', 'type': 'embed_reelgood' },
        { 'name': 'Snapcraft', 'type': 'embed_snapcraft' },
        { 'name': 'SoundCloud', 'type': 'embed_soundcloud' },
        { 'name': 'Spotify', 'type': 'embed_spotify' },
        { 'name': 'Tiktok', 'type': 'embed_tiktok' },
        { 'name': 'Tumblr', 'type': 'embed_tumblr' },
        { 'name': 'Twitch', 'type': 'embed_twitch' },
        { 'name': 'X', 'type': 'embed_twitter' },
        { 'name': 'Vimeo', 'type': 'embed_vimeo' },
        { 'name': 'Youtube', 'type': 'embed_youtube' },
        { 'name': 'Pinterest', 'type': 'embed_pinterest' },
        { 'name': 'Giphy', 'type': 'embed_giphy' },
        { 'name': 'Transistor', 'type': 'embed_transistor' },
        { 'name': 'Threads', 'type': 'embed_threads' },
        { 'name': 'BlueSky', 'type': 'embed_bluesky' },
        { 'name': 'Valnet Map', 'type': 'embed_valnet_map' }
    ];

    return embedTypes.sort((a, b) => {
        return a.name === b.name ? 0 : a.name < b.name ? -1 : 1;
    });
};

export type embedType = {
    name: string,
    type: string,
};

type embedRequest = {
    embed: string
    idBrand: number
    embedType: string
    embedContext?: string
    embedTypeOptions?: Array<string>
};



// WEBPACK FOOTER //
// ./js/utils/EmbedHelper.ts